import type { ColorInterpolator } from "../color-interpolator";
import type { HasBackgroundColor, PixelAccessor } from "../pixel-accessor";
import type { Color } from "../types";
import { blendColors } from "../utils";
import type { ColorResampler } from "./ColorResampler";
import type { ReversePixelMapper } from "../pixel-mapper";

/**
 * Simple ColorResampler implementation without resampling, using interpolation only.
 * It uses pixel color interpolation and works much faster than EllipticalWeightedAverage but produces aliasing
 * effects.
 * It is good for quick creation of distortion previews and also used for ReversePixelMapper implementations
 * that doesn't have partial derivatives.
 * It works most quickly when ImageAdapter.interpolationMethod is set to InterpolationMethod.INTEGER.
 */
export class PointResampler implements ColorResampler {
  /**
   * Image being resampled.
   */
  private readonly image: PixelAccessor & HasBackgroundColor;

  /**
   * Distortion mapper.
   */
  private readonly pixelMapper: ReversePixelMapper;

  /**
   * Matte color for invalid mappings.
   */
  private readonly matteColor: Color;

  /**
   * Output image scaling factor.
   */
  private scaling: number;

  /**
   * Color interpolator.
   * @private
   */
  private readonly interpolator: ColorInterpolator;

  /**
   * PointResampler constructor.
   *
   * @param image
   * @param pixelMapper
   * @param interpolator
   * @param matteColor
   */
  constructor(
    image: PixelAccessor & HasBackgroundColor,
    pixelMapper: ReversePixelMapper,
    interpolator: ColorInterpolator,
    matteColor: Color = [0, 0, 0, 0]
  ) {
    this.image = image;
    this.pixelMapper = pixelMapper;
    this.interpolator = interpolator;
    this.matteColor = matteColor;
    this.scaling = 1;
  }

  /**
   * @inheritDoc
   */
  getScaling(): number {
    return this.scaling;
  }

  /**
   * @inheritDoc
   */
  setScaling(scaling: number): this {
    this.scaling = scaling;
    return this;
  }

  /**
   * @inheritDoc
   */
  getResampledColor(x: number, y: number): Color {
    x = (x + 0.5) * this.scaling;
    y = (y + 0.5) * this.scaling;

    const validity = this.pixelMapper.getValidity(x, y, this.scaling);

    if (validity > 0) {
      const [u, v] = this.pixelMapper.reverseMap(x, y);
      const color = this.interpolator.interpolate(this.image, u - 0.5, v - 0.5);

      if (validity < 1) {
        return blendColors(color, this.matteColor, validity);
      }

      return color;
    }

    return this.matteColor;
  }
}
